import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { TransitionStatus } from 'react-transition-group';
import { Transition } from 'react-transition-group';

import { createElementWithId } from '@farmersdog/utils';

import styles from './Toast.module.css';
import transitionStyles from './transitions.module.css';

import { ToastAlert } from './ToastAlert';
import { ToastDialog } from './ToastDialog';
import { ToastConstant } from './constants';
import type { ToastObj, ToastOptions } from './types';

const portalRootId = `tfd-toast`;

export type ToastProps = ToastObj & ToastOptions;

/**
 * Create an alert dialog notification at the top of the screen.
 *
 * @cssVariable --tfd-toast-z-index
 * @see https://corgi-x.tfd.engineering/components/toast
 */
export function Toast(props: ToastProps) {
  const {
    isOpen,
    timeout = ToastConstant.Timeout,
    buttonLabel,
    autoClose = !props.buttonLabel,
    onClose,
    ...toast
  } = props;

  const [isInternallyOpen, setIsInternallyOpen] = useState(isOpen);

  const nodeRef = useRef(null);

  // Align internal state with prop
  useEffect(() => {
    setIsInternallyOpen(isOpen);
  }, [isOpen]);

  // This effect hides the toast after the timeout
  useEffect(() => {
    if (!autoClose) {
      return undefined;
    }

    let timeoutId = 0;
    if (isInternallyOpen) {
      timeoutId = window.setTimeout(() => setIsInternallyOpen(false), timeout);
      return () => clearTimeout(timeoutId);
    }

    return undefined;
  }, [autoClose, isInternallyOpen, isOpen, timeout]);

  // add a useState hook to avoid SSR hydration errors since this won't run
  // on the server or client until after the useEffect happens
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    setMounted(true);
  }, []);

  return mounted
    ? createPortal(
        <Transition
          nodeRef={nodeRef}
          in={isInternallyOpen}
          component={null}
          timeout={{
            enter: ToastConstant.TransitionEnter,
            exit: ToastConstant.TransitionExit,
          }}
          unmountOnExit
          onExited={onClose}
        >
          {(transitionStatus: TransitionStatus) => {
            if (!toast) {
              return <></>;
            }
            return (
              <div ref={nodeRef} className={transitionStyles[transitionStatus]}>
                {buttonLabel ? (
                  <ToastDialog
                    {...toast}
                    buttonLabel={buttonLabel}
                    onButtonClick={() => {
                      setIsInternallyOpen(false);
                      onClose?.();
                    }}
                  />
                ) : (
                  <ToastAlert {...toast} />
                )}
              </div>
            );
          }}
        </Transition>,
        createElementWithId(portalRootId, 'div', styles.portal)
      )
    : null;
}
